import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from '@/components/ui/input';

interface PriceInputProps {
  itemId: number;
  currentPrice: number;
  modifiedPrice?: number;
  onPriceChange: (itemId: number, price: number) => void;
  isActive?: boolean;
  onFocus?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export const PriceInput = React.memo(React.forwardRef<HTMLInputElement, PriceInputProps>(({ 
  itemId, 
  currentPrice, 
  modifiedPrice, 
  onPriceChange,
  isActive,
  onFocus: onFocusProp,
  onKeyDown: onKeyDownProp
}, ref) => {
  const [localValue, setLocalValue] = useState<string>(() => {
    const price = modifiedPrice !== undefined ? modifiedPrice : currentPrice;
    return price.toString();
  });
  const [isFocused, setIsFocused] = useState(false);
  const internalRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const lastExternalValue = useRef<number>(modifiedPrice !== undefined ? modifiedPrice : currentPrice);
  
  // Combine forwarded ref with internal ref
  const inputRef = useCallback((node: HTMLInputElement | null) => {
    internalRef.current = node;
    if (ref) {
      if (typeof ref === 'function') {
        ref(node);
      } else {
        ref.current = node;
      }
    }
  }, [ref]);

  // Only update local value when modified price changes externally AND we're not focused
  useEffect(() => {
    const externalPrice = modifiedPrice !== undefined ? modifiedPrice : currentPrice;
    
    // Only update if the value actually changed externally and we're not currently editing
    if (!isFocused && externalPrice !== lastExternalValue.current) {
      lastExternalValue.current = externalPrice;
      setLocalValue(externalPrice.toString());
    }
  }, [modifiedPrice, currentPrice, isFocused]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalValue(value);

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout for debounced update
    timeoutRef.current = setTimeout(() => {
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue >= 0) {
        // Only call onPriceChange if the value is different from currentPrice
        if (numValue !== currentPrice) {
          onPriceChange(itemId, numValue);
          lastExternalValue.current = numValue;
        }
      } else if (value === '') {
        // Only update to 0 if current price is not already 0
        if (currentPrice !== 0) {
          onPriceChange(itemId, 0);
          lastExternalValue.current = 0;
        }
      }
    }, 800); // Increased debounce to 800ms
  }, [itemId, onPriceChange, currentPrice]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    onFocusProp?.();
  }, [onFocusProp]);
  
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDownProp?.(e);
  }, [onKeyDownProp]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    // On blur, immediately update with current value
    const numValue = parseFloat(localValue);
    if (!isNaN(numValue) && numValue >= 0) {
      // Only call onPriceChange if the value is different from currentPrice
      if (numValue !== currentPrice) {
        onPriceChange(itemId, numValue);
        lastExternalValue.current = numValue;
      }
    } else {
      // Reset to current price if invalid
      const resetValue = modifiedPrice !== undefined ? modifiedPrice : currentPrice;
      setLocalValue(resetValue.toString());
      lastExternalValue.current = resetValue;
    }
  }, [localValue, itemId, onPriceChange, modifiedPrice, currentPrice]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const isModified = modifiedPrice !== undefined && modifiedPrice !== currentPrice;

  return (
    <div className="flex items-center gap-2">
      <span className="text-muted-foreground">₹</span>
      <Input
        ref={inputRef}
        type="number"
        value={localValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onWheel={(e) => {
          // Prevent accidental value changes from mouse wheel
          e.currentTarget.blur();
        }}
        className={`w-24 ${isModified ? 'bg-warning/5 border-warning/30' : ''} ${isActive ? 'ring-2 ring-primary ring-offset-1' : ''} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
        step="0.01"
        min="0"
      />
    </div>
  );
}), (prevProps, nextProps) => {
  // Custom comparison to prevent unnecessary re-renders
  return (
    prevProps.itemId === nextProps.itemId &&
    prevProps.currentPrice === nextProps.currentPrice &&
    prevProps.modifiedPrice === nextProps.modifiedPrice &&
    prevProps.onPriceChange === nextProps.onPriceChange &&
    prevProps.isActive === nextProps.isActive &&
    prevProps.onFocus === nextProps.onFocus &&
    prevProps.onKeyDown === nextProps.onKeyDown
  );
});

PriceInput.displayName = 'PriceInput';